import '@/lib/installSafeWebStorage';
import '@/styles/globals.css';
import type { AppProps } from 'next/app';

// add bootstrap css
import '@/styles/bootstrap.scss';
// add player css
import '@/styles/player.css';
import '@/styles/globals.css';
import dynamic from "next/dynamic";
import { AuthContextProvider } from "@/context/AuthContext";
import { ThemeProvider } from "react-bootstrap";
import { LayoutJSIncludes } from "@/layouts/LayoutJSIncludes";
import React, { useEffect, useRef, useState } from "react";
import { Router, useRouter } from 'next/router';
import { LanguageFlagContextProvider } from "@/context/LanguageFlagContext";
import { FavoriteSourceLanguagesProvider } from "@/context/FavoriteSourceLanguagesContext";
import AuthI18nProvider from '@/i18n/AuthI18nProvider';
import { GoogleTagManager } from '@next/third-parties/google';
import ConsentBanner from "@/components/consent/ConsentBanner";
import StripeFraudPreloadPages from "@/components/billing/StripeFraudPreloadPages";
import OnDemandSetupReturnHandler from "@/components/billing/OnDemandSetupReturnHandler";
import DesktopCaptureSetupBanner, { showsDesktopCaptureSetupBanner } from "@/components/auth/DesktopCaptureSetupBanner";
import { isAuthFlowPath } from "@/lib/consentBannerPaths";
import { captureDeferredPageview } from "@/lib/posthogClient";
const AppProductShell = dynamic(() => import("@/components/app/AppProductShell"));
import { bootstrapConsentWithGeo, hasAnalyticsConsent, isGpcActive } from "@/lib/consentCookie";
import { grantAnalyticsConsent, revokeAnalyticsConsent } from "@/lib/consentModeDefaults";
import { resolveGeoWithTier } from "@/lib/geoCountry";
import type { ConsentTier } from "@/lib/consentRegions";
const GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID || "";
export default function App({
  Component,
  pageProps
}: AppProps) {
  const router = useRouter();
  const lightAuthShell = isAuthFlowPath(router.pathname);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  // consentTier drives GTM loading strategy once geo resolves
  const [consentTier, setConsentTier] = useState<ConsentTier | null>(null);
  // Once GTM is mounted we never unmount it — only update consent mode state
  const gtmMountedRef = useRef(false);
  const [gtmMounted, setGtmMounted] = useState(false);
  useEffect(() => {
    // Resolve geo and bootstrap consent with three-tier logic
    resolveGeoWithTier().then(({
      consentTier: tier
    }) => {
      setConsentTier(tier);
      bootstrapConsentWithGeo(tier);
    });
    const syncAnalyticsConsent = () => {
      const hasConsent = hasAnalyticsConsent();
      setAnalyticsConsent(hasConsent);
    };
    syncAnalyticsConsent();
    document.addEventListener("braiv:consent", syncAnalyticsConsent);
    return () => document.removeEventListener("braiv:consent", syncAnalyticsConsent);
  }, []);

  // React to consent changes: upgrade or downgrade GA4 consent mode
  useEffect(() => {
    if (!gtmMountedRef.current) return;
    const handleConsent = (e: Event) => {
      const detail = (e as CustomEvent<{
        analytics: boolean;
      }>).detail;
      if (detail?.analytics) {
        grantAnalyticsConsent();
      } else {
        // Downgrade to cookieless pings rather than unloading GTM
        revokeAnalyticsConsent();
      }
    };
    document.addEventListener("braiv:consent", handleConsent);
    return () => document.removeEventListener("braiv:consent", handleConsent);
  }, [gtmMounted]);

  // Mount GTM once the tier + consent state are known
  useEffect(() => {
    if (!GTM_ID || gtmMountedRef.current || consentTier === null) return;
    if (isGpcActive()) return; // hard kill — never load GTM for GPC users

    const shouldMount = analyticsConsent ||
    // user has accepted (any tier)
    consentTier === "open" ||
    // auto-granted
    consentTier === "strict"; // consent mode pings — GTM loads with denied defaults

    if (shouldMount) {
      gtmMountedRef.current = true;
      setGtmMounted(true);
    }
  }, [consentTier, analyticsConsent]);

  // Listen for the strict consent mode signal (fired by bootstrapConsentWithGeo)
  useEffect(() => {
    const handleStrictMode = () => {
      if (GTM_ID && !gtmMountedRef.current && !isGpcActive()) {
        gtmMountedRef.current = true;
        setGtmMounted(true);
      }
    };
    document.addEventListener("braiv:consent-mode-strict", handleStrictMode);
    return () => document.removeEventListener("braiv:consent-mode-strict", handleStrictMode);
  }, []);
  useEffect(() => {
    const handleRouteChange = () => {
      void captureDeferredPageview();
    };
    void captureDeferredPageview();
    Router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);
  const page = <>
            <OnDemandSetupReturnHandler />
            <Component {...pageProps} />
        </>;
  return <AuthContextProvider data-sentry-element="AuthContextProvider" data-sentry-component="App" data-sentry-source-file="_app.tsx">
            <FavoriteSourceLanguagesProvider data-sentry-element="FavoriteSourceLanguagesProvider" data-sentry-source-file="_app.tsx">
                <LanguageFlagContextProvider data-sentry-element="LanguageFlagContextProvider" data-sentry-source-file="_app.tsx">
                    <ThemeProvider data-sentry-element="ThemeProvider" data-sentry-source-file="_app.tsx">
                        <LayoutJSIncludes data-sentry-element="LayoutJSIncludes" data-sentry-source-file="_app.tsx" />
                        {lightAuthShell ? <AuthI18nProvider>
                                {showsDesktopCaptureSetupBanner(router.pathname) ? <DesktopCaptureSetupBanner /> : null}
                                {page}
                            </AuthI18nProvider> : <AppProductShell>{page}</AppProductShell>}
                    </ThemeProvider>
                </LanguageFlagContextProvider>
            </FavoriteSourceLanguagesProvider>
            {GTM_ID && gtmMounted ? <GoogleTagManager gtmId={GTM_ID} /> : null}
            <StripeFraudPreloadPages data-sentry-element="StripeFraudPreloadPages" data-sentry-source-file="_app.tsx" />
            <ConsentBanner data-sentry-element="ConsentBanner" data-sentry-source-file="_app.tsx" />
        </AuthContextProvider>;
}